import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Image, Spinner, Alert, Table } from 'react-bootstrap';
import api from '../../api/axiosInstance';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { UserContext } from '../../UserContext';
import { isAdminType } from '../../utils/roleUtils';
import CompanyDetailHero from './companyDetail/CompanyDetailHero';
import CompanyDetailMobileTabs from './companyDetail/CompanyDetailMobileTabs';
import CompanyDetailSection from './companyDetail/CompanyDetailSection';
import JobsTabLabel from './companyDetail/JobsTabLabel';
import { isEmptyValue } from './companyDetail/emptyValue';
import icImgPreview from '../../assets/img/ic-img.svg';
import gptwBanner from '../../assets/img/gptw-banner.svg';
import icApproved from '../../assets/img/ic-check-circle.svg';
import icRejected from '../../assets/img/ic-close-circle.svg';
import icRLifecycleReview from '../../assets/img/ic-lyfecycle-review.svg';
import icUnderReview from '../../assets/img/ic-under-review.svg';

const DiscoveryJobsView = () => {
    const { id, cityId, companyId } = useParams();
    const { pathname } = useLocation();
    // /discovery/cities/:id/jobs/:companyId — id is cityId, no cityId param
    const isCityJobsContext = !pathname.includes('/companies/') && !pathname.includes('/countries/') && pathname.includes('/cities/') && pathname.includes('/jobs/');
    // /discovery/countries/:id/cities/:cityId/jobs/:companyId
    const isCountryCityJobsContext = !pathname.includes('/companies/') && pathname.includes('/countries/') && pathname.includes('/cities/') && pathname.includes('/jobs/');
    const actualCompanyId = companyId || id;
    const actualCityId = isCityJobsContext ? id : (cityId || null);
    const isCountryCityContext = isCountryCityJobsContext || Boolean(companyId && cityId && id && !isCityJobsContext && !isCountryCityJobsContext);
    const isCountryOnlyContext = !isCityJobsContext && !isCountryCityJobsContext && Boolean(companyId && !cityId && id);
    const countryCode = isCountryCityContext ? id : (isCountryOnlyContext ? id : null);

    const { user } = useContext(UserContext);
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const isAdminViewer = isAdminType(user?.role);

    const [company, setCompany] = useState(null);
    const [cityData, setCityData] = useState(null);
    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCompanyJobs = async () => {
            try {
                if (!isReconnect || !company) setLoading(true);
                setError('');
                const res = await api.get(`/api/discovery/companies/${actualCompanyId}/jobs`, { signal });
                if (!isCurrent()) return;
                setCompany(res.data);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching company jobs:', err);
                setError('An error occurred while fetching company jobs.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCompanyJobs();
    }, [actualCompanyId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!actualCityId) return;
        api.get(`/api/discovery/cities/${actualCityId}`, { signal })
            .then(res => { if (isCurrent()) setCityData(res.data); })
            .catch(() => {});
    }, [actualCityId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!isCountryOnlyContext) return;
        api.get(`/api/discovery/countries/${countryCode}`, { signal })
            .then(res => { if (isCurrent()) setCountryData(res.data); })
            .catch(() => {});
    }, [isCountryOnlyContext, countryCode]);

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-company',
        discoveryType: 'jobs',
        companyId: actualCompanyId,
        companyName: company?.title || company?.name || '',
        cityName: cityData?.cityName || cityData?.name || company?.cityRegion || '',
        countryName: countryData?.countryName || countryData?.name || '',
        activeTab: 'Jobs',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'jobs',
            companyName: company?.title || company?.name || '',
            selectedCity: cityData?.cityName || cityData?.name || company?.cityRegion || '',
            selectedCountry: countryData?.countryName || countryData?.name || company?.country || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!company) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">Company not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    const studentTotal = Number(company.openJobsTotal) || 0;
    const companyTotal = 0;

    const jobsTabLabel = companyTotal
        ? `Jobs ${companyTotal}`
        : studentTotal
            ? `Jobs ${studentTotal}`
            : 'Jobs';

    const companyPath = actualCityId
        ? (isCountryCityContext
            ? `/discovery/countries/${countryCode}/cities/${actualCityId}/companies/${actualCompanyId}`
            : `/discovery/cities/${actualCityId}/companies/${actualCompanyId}`)
        : (isCountryOnlyContext
            ? `/discovery/countries/${countryCode}/companies/${actualCompanyId}`
            : `/discovery/companies/${actualCompanyId}`);

    const navItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: jobsTabLabel },
    ];

    // This page already has studentTotal from its own /jobs fetch above, so
    // the mobile tab badge reuses it directly instead of firing another request.
    const mobileNavItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: <JobsTabLabel jobsCount={studentTotal} /> },
    ];

    const logo = company.logo || '';
    const urlInput = company.url || '';
    const approvalStatus = String(company.accountApprovalStatus || '').toLowerCase();

    const jobTypes = [
        { label: 'Student jobs', value: company.openJobsStudent },
        { label: 'Intern',        value: company.openJobsIntern },
        { label: 'Starting',      value: company.openJobsStarting },
        { label: '1-3 year',      value: company.openJobs13 },
        { label: '3+ year',       value: company.openJobs3 },
        { label: 'Total',         value: company.openJobsTotal },
    ];

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb
                                fromCountry={(isCountryCityContext && !isCityJobsContext) ? countryCode : null}
                                countryName={
                                    isCountryCityContext ? cityData?.country?.name :
                                    isCountryOnlyContext ? (countryData?.countryName || countryData?.name) :
                                    null
                                }
                                cityName={actualCityId ? (cityData?.cityName || cityData?.name) : null}
                                subPage={(isCityJobsContext || isCountryCityJobsContext) ? "Jobs" : (companyId ? "Companies" : null)}
                                companyName={company?.title || company?.name}
                                label={(isCityJobsContext || isCountryCityJobsContext) ? null : "Jobs"}
                            />
                        </Col>
                    </Row>

                    {!isMobile ? (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Card className="card-premium">
                                        <Card.Body>
                                            <Row className="align-items-center3">
                                                <Col>
                                                    <h3 className="text-start">1. General Info</h3>
                                                    <hr />
                                                </Col>
                                            </Row>
                                            <Row className="mb-4">
                                                <Col md={8} className="d-flex">
                                                    <div className="upload-image-wrapper">
                                                        <div className="upload-preview not-editing">
                                                            <Image
                                                                src={logo.trim() ? logo : icImgPreview}
                                                                alt={company.title || 'Company Logo'}
                                                                roundedCircle
                                                                className="preview-image"
                                                                onError={(e) => { e.currentTarget.src = icImgPreview; }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h1>{company.title || '—'}</h1>
                                                        <p>
                                                            {urlInput ? (
                                                                <a
                                                                    href={urlInput.startsWith('http') ? urlInput : `https://${urlInput}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {urlInput}
                                                                </a>
                                                            ) : '—'}
                                                        </p>
                                                        <div className="status-wrapper">
                                                            <p>
                                                                <span className={
                                                                    approvalStatus === 'approved' ? 'approved' :
                                                                    approvalStatus === 'rejected' ? 'rejected' :
                                                                    approvalStatus === 'lifecycle review' ? 'lifecycle-review' :
                                                                    approvalStatus === 'under review' ? 'under-review' : ''
                                                                }>
                                                                    <span className="d-flex align-items-center gap-2">
                                                                        {approvalStatus === 'approved' && <img src={icApproved} alt="Approved" />}
                                                                        {approvalStatus === 'rejected' && <img src={icRejected} alt="Rejected" />}
                                                                        {approvalStatus === 'lifecycle review' && <img src={icRLifecycleReview} alt="Lifecycle Review" />}
                                                                        {approvalStatus === 'under review' && <img src={icUnderReview} alt="Under Review" />}
                                                                        {company.accountApprovalStatus}
                                                                    </span>
                                                                </span>
                                                                <span className={`badge ${company.activityStatus ? 'bg-active' : 'bg-inactive'}`}>
                                                                    {company.activityStatus ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                                    {parseFloat(company.bestPlacetoWorkRating) > 89 && (() => {
                                                        const rating = parseFloat(company.bestPlacetoWorkRating || 0);
                                                        const redDash = rating;
                                                        const grayDash = 100 - rating;
                                                        return (
                                                            <div className="gptw-badge d-flex align-items-center">
                                                                <div className="gptw-logo">
                                                                    <Image src={gptwBanner} alt="Great Place to Work" />
                                                                </div>
                                                                <div className="gptw-text-circle">
                                                                    <div className="gptw-circle position-relative">
                                                                        <svg width="80" height="80" viewBox="0 0 36 36">
                                                                            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e0e0e0" strokeWidth="3" />
                                                                            <circle
                                                                                cx="18" cy="18" r="15.9155"
                                                                                fill="none" stroke="#e3000b" strokeWidth="3"
                                                                                strokeDasharray={`${redDash} ${grayDash}`}
                                                                                strokeLinecap="round"
                                                                                transform="rotate(-90 18 18)"
                                                                            />
                                                                            <text x="18" y="21" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">
                                                                                {rating}%
                                                                            </text>
                                                                        </svg>
                                                                    </div>
                                                                    <p className="gptw-text">Employees say this is a great place to work</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Row className="mb-4 mt-3">
                                        <Col>
                                            <NavTabs items={navItems} />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col>
                                            <h2>
                                                Jobs{' '}
                                                {companyTotal > 0 && (
                                                    <span className="text-primary fw-bold">{companyTotal} </span>
                                                )}
                                                {studentTotal > 0 && (
                                                    <span className="text-primary fw-bold">({studentTotal})</span>
                                                )}
                                            </h2>
                                            {studentTotal > 0 && (
                                                <p className="text-muted mb-0">
                                                    Total of {studentTotal} jobs imported by students
                                                    {company.hrRecrutingUrl && (
                                                        <>
                                                            {' — '}
                                                            <a
                                                                href={company.hrRecrutingUrl.startsWith('http') ? company.hrRecrutingUrl : `https://${company.hrRecrutingUrl}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-success"
                                                            >
                                                                view on company's website
                                                            </a>
                                                        </>
                                                    )}
                                                </p>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mb-4">
                                        <Col>
                                            <div className="table-responsive list">
                                                <Table className="discovery-table">
                                                    <thead>
                                                        <tr>
                                                            {jobTypes.map(({ label }) => (
                                                                <th key={label}><div>{label}</div></th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            {jobTypes.map(({ label, value }) => {
                                                                const n = Number(value) || 0;
                                                                return (
                                                                    <td key={label}>
                                                                        {n > 0
                                                                            ? <span className="text-primary fw-bold">{n}</span>
                                                                            : <span className="text-muted">n/a</span>
                                                                        }
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    ) : (
                        <div className="company-detail-mobile">
                            <CompanyDetailHero company={company} isAdminViewer={isAdminViewer} />

                            <CompanyDetailMobileTabs items={mobileNavItems} />

                            <Card className="company-detail-content-card mb-4">
                                <Card.Body>
                                    <h2 className="company-detail-content-title">
                                        Jobs{' '}
                                        {studentTotal > 0 && (
                                            <span className="text-primary fw-bold">({studentTotal})</span>
                                        )}
                                    </h2>
                                    {studentTotal > 0 && (
                                        <p className="text-muted mb-4">
                                            Total of {studentTotal} jobs imported by students
                                            {company.hrRecrutingUrl && (
                                                <>
                                                    {' — '}
                                                    <a
                                                        href={company.hrRecrutingUrl.startsWith('http') ? company.hrRecrutingUrl : `https://${company.hrRecrutingUrl}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-success"
                                                    >
                                                        view on company's website
                                                    </a>
                                                </>
                                            )}
                                        </p>
                                    )}

                                    <CompanyDetailSection
                                        title="Job availability"
                                        showSeparatorWithoutNumber={Boolean(cityId)}
                                    >
                                        <div className="company-detail-jobs-grid">
                                            {jobTypes.map(({ label, value }) => (
                                                <div key={label} className="company-detail-job-metric">
                                                    <span className="company-detail-job-metric-label">
                                                        {label === 'Starting' ? 'Entry' : label}
                                                    </span>
                                                    <strong className={isEmptyValue(value) ? 'text-muted' : 'text-primary'}>
                                                        {isEmptyValue(value) ? 'n/a' : value}
                                                    </strong>
                                                </div>
                                            ))}
                                        </div>
                                    </CompanyDetailSection>
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryJobsView;
